<script setup lang="ts">
import { ref, onMounted, onUnmounted, markRaw, watch } from 'vue'
import axios, { baseUrl } from '@/api'
import { Device } from '@twilio/voice-sdk'
import type { Call } from '@twilio/voice-sdk'
import type {
  Agent,
  Team,
  CallinglyCall,
  CallerId,
  Lead,
  AudioSettings,
  CallUser,
  LaravelMeta,
} from './types/types'
import { RouterView } from 'vue-router'
import { storeToRefs } from 'pinia'
import NavBar from '@/components/NavBar.vue'
import TabHistory from '@/components/TabHistory.vue'
import { useUserStore } from '@/stores/user'
import { usePusherStore } from '@/stores/pusher'
import { useStatusStore } from '@/stores/status'
import { useDeviceStore } from '@/stores/device'
import { useErrorsStore } from '@/stores/errors'
import { useOnlineStore } from '@/stores/online'
import { useRegisteredStore } from '@/stores/registered'
import * as Sentry from '@sentry/vue'
import { version } from '../package.json'
//import type { PostHog } from 'posthog-js'
//const $posthog = inject('$posthog') as PostHog
import 'vue-sonner/style.css'
import { Toaster, toast } from 'vue-sonner'

const userStore = useUserStore()
const { user } = storeToRefs(userStore)

const statusStore = useStatusStore()
const { status } = storeToRefs(statusStore)

const deviceStore = useDeviceStore()
const { device } = storeToRefs(deviceStore)

const pusherStore = usePusherStore()

const errorsStore = useErrorsStore()
const { errors } = storeToRefs(errorsStore)

const onlineStore = useOnlineStore()
const { online } = storeToRefs(onlineStore)

const registeredStore = useRegisteredStore()
const { registered } = storeToRefs(registeredStore)

//@ts-expect-error: Only available in electron
//we only need this code for the old electron app 1.2.1 and below
//after a few months of 1.4.0 we can remove this code
let ipcRenderer

if (/electron/i.test(navigator.userAgent)) {
  //@ts-expect-error: Only available in electron
  if (window.require) {
    //@ts-expect-error: Only available in electron
    ipcRenderer = window.require('electron').ipcRenderer
  }
}

// State variables (converted from data())
const agents = ref<Agent[]>([])
const agents_meta = ref<LaravelMeta>({} as LaravelMeta)
const agents_status = ref('online')
const agents_search = ref(null)
const agent_id = ref<number | null>(null)
const teams = ref<Team[]>([])
const team_id = ref<number | null>(null)
const call = ref<CallinglyCall | null>(null)
const call_user = ref<CallUser | null>(null)
const call_user_id = ref<number | null>(null)
const caller_ids = ref<CallerId[]>([])
const caller_id = ref<number | null>(null)
const error_message = ref<string | null>(null)
const message = ref('Connecting...')
const note = ref<string>('')
const seconds = ref<number>(-1)
const timer = ref<number | null>(null)
const lead = ref<Partial<Lead>>({})
const isChecking = ref(false)
const isMuted = ref(false)
const isOnHold = ref<boolean>(false)
const isDialpadOpen = ref(false)
const isTransferOpen = ref(false)
const isTransferStarted = ref(false)
const isConferenceLeadOpen = ref(false)
const isMakingCall = ref(false)
const openTab = ref<string | null>(null)
const email = ref<string | null>(null)
const password = ref<string | null>(null)
const fname = ref<string | null>(null)
const lname = ref<string | null>(null)
const phone_number = ref<string | null>(null)
const autoaccept = ref(false)
const transfer_type = ref('user')
const audio = ref<AudioSettings>({
  input: [],
  output: [],
  microphoneDevice: null,
  speakerDevice: null,
  ringtoneDevice: null,
  inputVolume: 0,
  outputVolume: 0,
})
const smsbody = ref("Sorry we couldn't reach you!")
const twilioCall = ref<Call | null>(null)
const pingInterval = ref<number | null>(null)
const dispositionCountdown = ref<number>(60)
const dispositionTimer = ref<number | null>(null)
const DISPOSITION_SECONDS_TO_WAIT = 60

const crmLinks: Record<string, string> = {
  hubspot: 'Hubspot',
  salesforce: 'Salesforce',
  activecampaign: 'ActiveCampaign',
  followupboss: 'Follow Up Boss',
}

// Ping the server every 60 seconds when user is logged in and device is registered
const ping = async () => {
  try {
    await axios.post('/api/v1/user/ping')
  } catch (err) {
    console.error('Ping failed:', err)
  }
}

// Notify the server when the user disconnects (logout or app close)
const disconnect = () => {
  if (!user.value?.id) return
  navigator.sendBeacon(`${baseUrl}/api/v1/user/disconnect`)
}

const startPingInterval = () => {
  if (pingInterval.value) return
  ping() // Ping immediately
  pingInterval.value = window.setInterval(ping, 60000)
}

const stopPingInterval = () => {
  if (pingInterval.value) {
    clearInterval(pingInterval.value)
    pingInterval.value = null
  }
}

watch(
  [user, registered],
  ([newUser, newRegistered]) => {
    if (newUser?.id && newRegistered) {
      startPingInterval()
    } else {
      stopPingInterval()
    }
  },
  { immediate: true },
)

// Watch for disposition status to start countdown timer
watch(status, (newStatus, oldStatus) => {
  // Clear any existing disposition timer
  if (dispositionTimer.value) {
    clearInterval(dispositionTimer.value)
    dispositionTimer.value = null
  }

  // Start countdown when entering disposition screen
  if (newStatus === 'disposition') {
    dispositionCountdown.value = DISPOSITION_SECONDS_TO_WAIT
    dispositionTimer.value = window.setInterval(() => {
      dispositionCountdown.value--
      if (dispositionCountdown.value <= 0) {
        if (dispositionTimer.value) {
          clearInterval(dispositionTimer.value)
          dispositionTimer.value = null
        }
        reset()
      }
    }, 1000)
  }
})

const login = async () => {
  const response = await axios.post('/api/v1/user/login', {
    email: email.value,
    password: password.value,
  })

  if (response.data.success) {
    location.reload()
  } else {
    error_message.value = 'Your login credentials are invalid.'
  }
}

const startPhone = async (attempt = 1) => {
  const maxAttempts = 3
  let token = null

  try {
    console.log('startPhone Fetching token...')

    const response = await axios.get('/api/v1/twilio/token?platform=desktop')

    if (!response.data?.token) {
      Sentry.captureException(new Error('startPhone Invalid token received'))
      return
    }

    token = response.data.token
    console.log('startPhone Token received')
  } catch (error) {
    if (attempt < maxAttempts) {
      console.error(
        `startPhone Attempt ${attempt} of ${maxAttempts} failed to fetch token. Retrying in 10 seconds...`,
      )
      window.setTimeout(() => {
        startPhone(attempt + 1)
      }, 10000)
    } else {
      console.error(
        `startPhone Attempt ${attempt} of ${maxAttempts} failed to fetch token. All retry attempts exhausted.`,
      )
      //Sentry.captureException(error)
    }

    return
  }

  device.value = markRaw(
    new Device(token, {
      // 0 = trace, 1 = debug, 2 = info, 3 = warn, 4 = error, 5 = silent
      logLevel: user.value?.account_id === 1 ? 1 : 4,
      sounds: {
        incoming: 'https://cdn.callingly.com/mp3s/us-ring2.mp3',
      },
      enableImprovedSignalingErrorPrecision: true,
    }),
  )

  setupDeviceHandlers()

  device.value.register()
  device.value?.audio?.disconnect(false)
}

const refreshtoken = async (attempt = 1) => {
  const maxAttempts = 3

  console.log('refreshtoken Refreshing token...')

  try {
    const response = await axios.get('/api/v1/twilio/token?platform=desktop')

    if (!response.data?.token) {
      Sentry.captureException(new Error('refreshtoken Invalid token received'))

      return
    }

    device.value?.updateToken(response.data.token)
    console.log('refreshtoken Token refreshed')
  } catch (error) {
    if (attempt < maxAttempts) {
      console.error(
        `refreshtoken Attempt ${attempt} of ${maxAttempts} failed to refresh token. Retrying in 10 seconds...`,
      )
      window.setTimeout(() => {
        refreshtoken(attempt + 1)
      }, 10000)
    } else {
      console.error(
        `refreshtoken Attempt ${attempt} of ${maxAttempts} failed to refresh token. All retry attempts exhausted.`,
      )
      //Sentry.captureException(error)
    }

    return
  }
}

const getAudioPermissions = async () => {
  // Call getUserMedia to ask for device permission and populate the device labels.
  // Also, performing this action here allows for capturing gUM (getUserMedia) errors early
  // before accepting/receiving a call and it's possible to create a much better user experience.
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true })

  // Calling getUserMedia will start the media track selected.
  // This is not desired as the user may get the impression the mic is in use.
  // Therefore, we want to avoid having tracks started when they're not needed.
  // We only wanted to get the input device list so we stop the tracks immediately.
  stream.getTracks().forEach((track) => track.stop())
}

const updateAudioDevicesList = async () => {
  audio.value.input = []
  audio.value.output = []

  device.value?.audio?.availableInputDevices.forEach((dev: MediaDeviceInfo, id: string) => {
    audio.value.input.push(dev)
  })

  device.value?.audio?.availableOutputDevices.forEach((dev: MediaDeviceInfo, id: string) => {
    audio.value.output.push(dev)
  })

  // make sure the selected microphone device is still available
  if (audio.value.microphoneDevice) {
    if (!audio.value.input.find((dev) => dev.deviceId === audio.value.microphoneDevice)) {
      audio.value.microphoneDevice = null
    }
  }

  // make sure the selected speaker device is still available
  if (audio.value.speakerDevice) {
    if (!audio.value.output.find((dev) => dev.deviceId === audio.value.speakerDevice)) {
      audio.value.speakerDevice = null
    }
  }

  // make sure the selected ringtone device is still available
  if (audio.value.ringtoneDevice) {
    if (!audio.value.output.find((dev) => dev.deviceId === audio.value.ringtoneDevice)) {
      audio.value.ringtoneDevice = null
    }
  }

  // update the selected microphone device if it is not set or the device is no longer available
  if (!audio.value.microphoneDevice) {
    audio.value.microphoneDevice = audio.value.input[0]?.deviceId ?? 'default'
  }

  // update the selected speaker device if it is not set or the device is no longer available
  if (!audio.value.speakerDevice) {
    audio.value.speakerDevice =
      device.value?.audio?.speakerDevices.get().values().next().value?.deviceId ??
      audio.value.output[0]?.deviceId ??
      'default'
  }

  // update the selected ringtone device if it is not set or the device is no longer available
  if (!audio.value.ringtoneDevice) {
    audio.value.ringtoneDevice =
      device.value?.audio?.ringtoneDevices.get().values().next().value?.deviceId ??
      audio.value.output[0]?.deviceId ??
      'default'
  }
}

const setupDeviceHandlers = () => {
  if (!device.value) {
    console.error('setupDeviceHandlers failed! No device value.')
    return
  }

  console.log('Setting device handlers...')

  device.value.setMaxListeners(5)

  device.value.on('registered', () => {
    console.log('Device registered')
    registered.value = true
    updateAudioDevicesList()
    reset()
  })

  // We want to detect if the device list changes e.g. a headset was plugged in/out.
  // We set up handlers to update our dropdown list with the new device list
  // Subscribe to the event for when the list of devices changes
  device.value.audio?.on('deviceChange', () => updateAudioDevicesList())

  device.value.on('unregistered', function () {
    console.log('Device unregistered')
    registered.value = false
    disconnect()

    if (user.value && online.value) {
      reset()
      startPhone()
    }
  })

  device.value.on('error', async function (error) {
    errorsStore.addError(error)

    // 31000: General Twilio Voice SDK error.
    // 31005: WebSocket connection to Twilio's signaling servers were unexpectedly ended.
    // 31009: No transport available to send or receive messages.
    if (error.code == 31000 || error.code == 31005 || error.code == 31009) {
      return
    }

    reset()
    if (error.code == 31401 || error.code == 31402) {
      toast.error('Callingly is not able to access your microphone.')
    }

    // 31205: ERROR: JWT Token Expired
    if (error.code == 31205) {
      console.log('Error 31205: JWT Token Expired')
      Sentry.captureException(error)
      await refreshtoken()

      // 20101: ERROR: Invalid Access Token
    } else if (error.code == 20101) {
      console.log('Error 20101:  Invalid Access Token')
      //Sentry.captureException(error)

      // destroy the device to force a new token to be fetched
      if (registered.value) {
        console.log('Device registered... Destroying device...')
        if (device.value) {
          device.value.destroy()
        }
      } else {
        console.log('Device not registered... Resetting and starting phone...')
        reset()
        startPhone()
      }

      // 20104: WARNING: Access Token expired or expiration date invalid
    } else if (error.code == 20104) {
      console.log('Error 20104: Access Token expired or expiration date invalid')
      //Sentry.captureException(error)

      // destroy the device to force a new token to be fetched
      if (registered.value) {
        console.log('Device registered... Destroying device...')
        if (device.value) {
          device.value.destroy()
        }
      } else {
        console.log('Device not registered... Resetting and starting phone...')
        reset()
        startPhone()
      }
    } else {
      Sentry.captureException(error)
      toast.error(`Device error. Code: ${error.code}`)
      //throw error
    }
  })

  device.value.on('tokenWillExpire', async () => {
    await refreshtoken()
  })

  device.value.on('incoming', function (_call: Call) {
    console.log('Incoming call', twilioCall.value?.parameters.From)
    twilioCall.value = markRaw(_call)
    setupCallHandlers()
    incomingLead(Number(twilioCall.value.parameters.From.replace('client:', '')))
  })
}

const reset = () => {
  if (timer.value) {
    clearInterval(timer.value)
  }
  if (dispositionTimer.value) {
    clearInterval(dispositionTimer.value)
    dispositionTimer.value = null
  }
  message.value = ''

  //call.value = null
  //call_user.value = null

  isMuted.value = false
  isDialpadOpen.value = false
  isTransferOpen.value = false
  isTransferStarted.value = false
  status.value = 'waiting'
  lead.value = {}
}

const handleOnlineStatusChange = () => {
  online.value = navigator.onLine

  if (!online.value) {
    console.log('Network offline. Checking in 10 seconds if still offline...')

    window.setTimeout(() => {
      if (!online.value) {
        console.log('Network offline after timeout. Destroying device...')
        reset()
        if (device.value) {
          device.value.destroy()
        }
      } else {
        console.log('Network online after timeout. Nothing to be done.')
      }
    }, 10000)
  } else {
    console.log('Network online')

    if (!registered.value) {
      console.log('Device not registered... Resetting and starting phone...')
      reset()
      startPhone()
    }
  }
}

// Lifecycle hooks
onMounted(async () => {
  await userStore.getUser()

  if (!userStore.user?.id) {
    return
  }

  /*
  if ($posthog) {
    $posthog.identify(`user-${userStore.user.id}`, {
      firstName: userStore.user.fname,
      lastName: userStore.user.lname,
      email: userStore.user.email,
      createdAt: userStore.user.created_at,
      role: userStore.user.role,
    })
  }
  */

  Sentry.setUser({
    id: userStore.user.id,
    email: userStore.user.email,
  })

  await getAudioPermissions()
  getCallerIds()
  getAgents()
  startPhone()

  const tel = new URLSearchParams(window.location.search).get('tel')
  if (tel && tel.startsWith('tel:')) {
    const parsedTel = tel.replace(/[^\+0-9]/g, '')
    toggleNewCall(undefined, parsedTel)
  }

  pusherStore.init()

  window.addEventListener('online', handleOnlineStatusChange)
  window.addEventListener('offline', handleOnlineStatusChange)
  window.addEventListener('beforeunload', disconnect)
})

onUnmounted(() => {
  window.removeEventListener('online', handleOnlineStatusChange)
  window.removeEventListener('offline', handleOnlineStatusChange)
  window.removeEventListener('beforeunload', disconnect)
  stopPingInterval()
  disconnect()
})

// Call handling methods
const acceptCall = () => {
  if (!twilioCall.value) {
    console.error('acceptCall failed! No twilioCall value.')
    return
  }
  if (twilioCall.value.direction !== 'OUTGOING') {
    setAudioDevices()
    twilioCall.value.accept()
  }

  if (
    call_user.value?.type === 'listen' ||
    call_user.value?.type === 'whisper' ||
    call_user.value?.type === 'barge'
  ) {
    seconds.value = call.value?.seconds_since_started_at ?? -1
  } else {
    seconds.value = -1
  }

  timerFunction()
  if (timer.value) {
    clearInterval(timer.value)
  }
  timer.value = window.setInterval(timerFunction, 1000)
  status.value = 'oncall'
}

const reject = async () => {
  if (timer.value) {
    clearInterval(timer.value)
  }

  await axios.post(`/apps/webrtc/${call.value?.id}/reject`)
  twilioCall.value?.reject()
  reset()
}

const hangup = () => {
  if (twilioCall.value?.status() !== 'open') {
    reset()
  }
  isTransferOpen.value = false
  twilioCall.value?.disconnect()
}

const mute = () => {
  isMuted.value = true
  twilioCall.value?.mute(true)
}

const unmute = () => {
  isMuted.value = false
  twilioCall.value?.mute(false)
}

const digitPress = (digit: string) => {
  twilioCall.value?.sendDigits(digit)
}

// UI interaction methods
const toggleSettings = () => {
  if (openTab.value === 'settings') {
    openTab.value = null
  } else {
    openTab.value = 'settings'

    updateAudioDevicesList()
  }
}

const setAudioDevices = () => {
  device.value?.audio?.setInputDevice(audio.value.microphoneDevice ?? 'default')

  if (audio.value.speakerDevice) {
    device.value?.audio?.speakerDevices.set(audio.value.speakerDevice)
  }

  if (audio.value.ringtoneDevice) {
    device.value?.audio?.ringtoneDevices.set(audio.value.ringtoneDevice)
  }
}

const toggleHistory = () => {
  if (openTab.value === 'history') {
    openTab.value = null
  } else {
    openTab.value = 'history'
  }
}

const toggleNewCall = (lead?: Lead, number?: string) => {
  error_message.value = null
  if (lead) {
    fname.value = lead.fname
    lname.value = lead.lname
    phone_number.value = lead.phone_number
  } else if (number) {
    fname.value = null
    lname.value = null

    // number already includes the country code so just use what we have
    if (number.includes('+')) {
      phone_number.value = number
    } else {
      // add users country code if available
      if (user.value?.country_dial_code) {
        phone_number.value = '+' + user.value?.country_dial_code + number
      } else {
        // otherwise add the default country code from the vue-tel-input
        phone_number.value = phone_number.value + number
      }
    }
  } else {
    fname.value = null
    lname.value = null
    phone_number.value = null
  }

  openTab.value = openTab.value === 'new_call' ? null : 'new_call'
}

const toggleDialpad = () => {
  isDialpadOpen.value = !isDialpadOpen.value

  if (isDialpadOpen.value) {
    isConferenceLeadOpen.value = false
    isTransferOpen.value = false
  }
}

const toggleTransfer = () => {
  isTransferOpen.value = !isTransferOpen.value
  if (isTransferOpen.value) {
    getTeams()
    getAgents()
    isDialpadOpen.value = false
  }
}

const toggleConferenceLead = () => {
  isConferenceLeadOpen.value = !isConferenceLeadOpen.value
  if (isConferenceLeadOpen.value) {
    isDialpadOpen.value = false
  }
}

const toggleConference = () => {
  if (openTab.value == 'conference') {
    openTab.value = null
  } else {
    openTab.value = 'conference'
    getTeams()
    getAgents()
  }
}

const timerFunction = () => {
  seconds.value++

  const min = Math.floor(seconds.value / 60)
  const sec = seconds.value - min * 60

  const formattedMin = min < 10 ? `0${min}` : min
  const formattedSec = sec < 10 ? `0${sec}` : sec

  message.value = `${formattedMin}:${formattedSec}`
}

// API methods
const getAgents = async (page = 1) => {
  if (page < 1) return

  const response = await axios.get('/api/v1/users/paginated', {
    params: {
      page,
      status: agents_status.value,
      search: agents_search.value,
      team_id: team_id.value,
    },
  })
  agents.value = response.data.data
  agents_meta.value = response.data.meta
}

const getTeams = async () => {
  const response = await axios.get('/api/v1/teams')
  teams.value = response.data.data
}

const getCallerIds = async () => {
  const response = await axios.get('/api/v1/callerids')
  caller_ids.value = response.data
}

// Conference and transfer methods
const startConference = async (agent: Agent) => {
  const response = await axios.post('/api/v1/calls/conference', {
    user_id: agent.id,
  })

  if (response.data.success) {
    openTab.value = null
    lead.value = {
      name: 'Conference',
    }
  }
}

const startTransfer = async (agent: Agent | null, team: Team | null) => {
  const request = agent
    ? {
        user_id: agent?.id,
        call_user_id: call_user_id.value,
      }
    : {
        team_id: team?.id,
        call_user_id: call_user_id.value,
      }

  if (agent) {
    agent.on_call_id = 1
  }

  const response = await axios.post(`/api/v1/calls/${call.value?.id}/transfer`, request)
  if (response.data.success) {
    isTransferOpen.value = false
    isTransferStarted.value = true
  }
}

const startListen = async (callId: number) => {
  const response = await axios.post(`/api/v1/calls/${callId}/listen`)
  if (response.data.success) {
    openTab.value = null
  }
}

const startWhisper = async (callId: number) => {
  const response = await axios.post(`/api/v1/calls/${callId}/whisper`)
  if (response.data.success) {
    openTab.value = null
  }
}

const startBarge = async (callId: number) => {
  const response = await axios.post(`/api/v1/calls/${callId}/barge`)
  if (response.data.success) {
    openTab.value = null
  }
}

const hold = () => {
  if (isOnHold.value) {
    return
  }

  isOnHold.value = true

  axios
    .put(`/api/v1/calls/${call.value?.id}/hold`)
    .then(() => {
      isOnHold.value = true
    })
    .catch((err) => {
      isOnHold.value = true
      console.error(err)
    })
}

const unhold = () => {
  if (!isOnHold.value) {
    return
  }

  isOnHold.value = false

  axios
    .put(`/api/v1/calls/${call.value?.id}/unhold`)
    .then(() => {
      isOnHold.value = false
    })
    .catch((err) => {
      isOnHold.value = true
      console.error(err)
    })
}

// Disposition methods
const thankyou = () => {
  status.value = 'thankyou'
}

const disposition = async (dispositionStatus: string) => {
  if (call.value?.id) {
    await axios.post(`/apps/webrtc/${call.value?.id}/disposition`, {
      status: dispositionStatus,
    })
  } else {
    Sentry.captureException(new Error('Disposition with no call id'))
  }

  if (
    user.value?.permissions.includes('app-sms') &&
    (dispositionStatus === 'voicemail' || dispositionStatus === 'missed')
  ) {
    status.value = 'sms'
  } else if (call.value?.profile?.is_deal_whispertext && dispositionStatus === 'contacted') {
    status.value = 'disposition2'
  } else {
    thankyou()
  }
}

const disposition2 = async (key: string) => {
  await axios.post(`/apps/webrtc/${call.value?.id}/disposition2`, { key })
  thankyou()
}

const saveNote = async () => {
  await axios.post(`/api/v1/notes`, { content: note.value, call_id: call.value?.id })
  note.value = ''
  reset()
}

const skipNote = async () => {
  note.value = ''
  reset()
}

// SMS methods
const getSMSMessage = () => {
  smsbody.value = "Sorry we couldn't reach you!"
}

const showsms = () => {
  status.value = 'sms'
}

const sendsms = async () => {
  const response = await axios.post(`/apps/webrtc/${call.value?.id}/sms`, {
    smsbody: smsbody.value,
  })

  if (response.data.success) {
    smsbody.value = "Sorry we couldn't reach you!"
    thankyou()
  } else {
    error_message.value = response.data.error
  }
}

// Retry methods
const retry = async () => {
  if (!lead.value) return

  const response = await axios.post('/api/v1/calls/twilio', {
    fname: lead.value.fname,
    lname: lead.value.lname,
    phone_number: lead.value.phone_number,
  })

  if (response.data.success) {
    status.value = 'waiting'
    lead.value = response.data.call.lead
  } else {
    error_message.value = response.data.error
  }
}

// Conference lead methods
const callConferenceLead = async () => {
  const response = await axios.post('/api/v1/calls/conferencelead', {
    call_id: call.value?.id,
    fname: fname.value,
    lname: lname.value,
    phone_number: phone_number.value,
  })

  if (response.data.success) {
    toggleConferenceLead()
    call.value = response.data.call
    call_user.value = response.data
    lead.value = response.data.lead
  } else {
    error_message.value = response.data.error
  }
}

const check = () => {
  if (isChecking.value) {
    return
  }

  isChecking.value = true

  axios
    .post('/apps/webrtc/check')
    .catch((err) => {
      console.error(err)
    })
    .finally(() => {
      isChecking.value = false
    })
}

const getCallinglyCall = async (callUserId: number) => {
  console.log('Getting Callingly Call with callUserId', callUserId)
  const response = await axios.get(`/api/v1/callusers/${callUserId}`)
  call.value = response.data.call
  call_user.value = response.data
  call_user_id.value = callUserId
  autoaccept.value = response.data.autoaccept
}

const incomingLead = async (callUserId: number) => {
  console.log('Incoming lead with callUserId', callUserId)

  // close the open tab if it's open
  if (openTab.value) {
    openTab.value = null
  }

  await getCallinglyCall(callUserId)

  if (call.value?.type === 'conference') {
    lead.value = {
      name: 'Conference',
    }
  } else {
    if (call.value?.lead) {
      lead.value = call.value.lead
    }

    if (user.value?.permissions.includes('app-sms')) {
      getSMSMessage()
    }
  }

  if (/electron/i.test(navigator.userAgent)) {
    //@ts-expect-error: Only available in electron
    if (ipcRenderer) {
      //we only need this code for the old electron app 1.2.1 and below
      //after a few months of 1.4.0 we can remove this code
      ipcRenderer.invoke('incoming-call')
    } else {
      //@ts-expect-error: Only available in electron
      // this is the new electron app 1.4.0 and above
      window.electronAPI?.incomingCall()
    }
  }

  status.value = 'ringing'

  if (call.value?.direction == 'inbound') {
    message.value = call.value.number.name

    if (call.value.number.account_id == 9420) {
      message.value = `${call.value.number.name} - ${call.value.number.phone_number_formatted}`
    }
  } else if (call.value?.type == 'transfer') {
    message.value = 'Incoming Transfer!'
  } else if (call.value?.type == 'conference') {
    message.value = 'Incoming Conference!'
  } else {
    message.value = 'Incoming Lead!'
  }

  if (
    (call.value?.account_id == 10787 || call.value?.account_id == 3998) &&
    user.value?.id != 25846
  ) {
    acceptCall()
  }

  if (autoaccept.value && call.value?.profile_id !== 7703) {
    acceptCall()
  }
}

// Make outbound call
const makeOutboundCall = async () => {
  if (!online.value || !registered.value) {
    return
  }

  console.log('Making outbound call')
  isMakingCall.value = true

  try {
    const response = await axios.post('/api/v1/calls/twilio/outbound', {
      fname: fname.value,
      lname: lname.value,
      phone_number: phone_number.value,
      agent_id: agent_id.value,
      interface: 'desktop',
    })

    if (response.data.success) {
      toggleNewCall()
      agent_id.value = null
      await getCallinglyCall(response.data.call_user_id)
      lead.value = response.data.call.lead

      setAudioDevices()

      const _call = await device.value?.connect({
        params: {
          call_user_id: response.data.call_user_id,
          caller_id: '' + caller_id.value,
          from: 'from',
          To: response.data.call.phone_number,
        },
      })

      if (_call) {
        twilioCall.value = markRaw(_call)
        acceptCall()
        message.value = 'Dialing...'
      }

      if (call_user.value?.type === 'listen') {
        mute()
      }

      setupCallHandlers()
    } else {
      error_message.value = response.data.error
    }
  } catch (err) {
    console.error(err)
  } finally {
    isMakingCall.value = false
  }
}

const setupCallHandlers = () => {
  if (!twilioCall.value) {
    console.error('setupCallHandlers failed! No twilioCall value.')
    return
  }

  console.log('Setting up call handlers...')

  twilioCall.value.setMaxListeners(4)

  twilioCall.value.on('cancel', () => {
    reset()
  })

  twilioCall.value.on('disconnect', async () => {
    await device.value?.audio?.unsetInputDevice()
    isDialpadOpen.value = false
    isMuted.value = false

    if (twilioCall.value && twilioCall.value.status() !== 'closed') {
      axios.post('/twilio/debug/webrtc', {
        user: user.value,
        conn_status: twilioCall.value.status(),
      })
    }

    if (timer.value) {
      clearInterval(timer.value)
    }
    message.value = 'Call Ended'

    if (
      call.value?.direction === 'inbound' ||
      call_user.value?.type === 'listen' ||
      call_user.value?.type === 'whisper'
    ) {
      status.value = 'thankyou'
    } else {
      if (call.value?.id) {
        status.value = 'disposition'
      } else {
        status.value = 'thankyou'
      }
    }

    await refreshtoken()
  })

  twilioCall.value.on('error', (error) => {
    toast.error(`Error during call. Code: ${error.code}`)
    errorsStore.addError(error)
    Sentry.captureException(error)
    reset()
  })

  twilioCall.value.on('volume', (inputVolume: number, outputVolume: number) => {
    audio.value.inputVolume = inputVolume * 10
    audio.value.outputVolume = outputVolume * 20
  })
}

const checkVersion = () => {
  console.log('Checking version...')
  fetch('/version?t=' + Date.now())
    .then((response) => response.text())
    .then((remoteVersion) => {
      const remoteVersionParts = remoteVersion.split('.')
      const localVersionParts = version.split('.')

      let newVersion = false
      for (let i = 0; i < remoteVersionParts.length; i++) {
        if (remoteVersionParts[i] > localVersionParts[i]) {
          newVersion = true
          break
        }
      }

      if (newVersion) {
        console.log(`A new version ${remoteVersion} is available.`)
        window.location.reload()
      } else {
        console.log(`You are running the latest version ${version}`)
      }
    })
}

const handleDoneClick = () => {
  reset()
  checkVersion()
}
</script>

<template>
  <main style="height: 100vh; max-height: 622px; width: 450px; margin: auto; position: relative">
    <Toaster richColors position="top-right" closeButtonPosition="top-right" closeButton />
    <NavBar />

    <div
      class="main-container"
      style="background-size: cover; height: calc(100% - 70px); padding-top: 20px"
    >
      <RouterView />
      <div v-if="status == 'login'">
        <h1 style="font-weight: 300; padding-bottom: 40px" class="text-center">Login</h1>

        <div
          v-if="error_message"
          class="help-block text-center"
          style="color: #a94442; margin-top: -30px; margin-bottom: 30px"
        >
          <strong>{{ error_message }} </strong>
        </div>

        <form
          class="login"
          role="form"
          method="POST"
          style="max-width: 400px; margin: auto"
          @submit.prevent="login"
        >
          <div class="form-group">
            <input v-model="email" type="email" name="email" value="" required autofocus />
            <span class="bar" />
            <label for="email">E-Mail Address</label>
          </div>

          <div class="form-group">
            <input v-model="password" type="password" name="password" required />
            <span class="bar" />
            <label for="password">Password</label>
          </div>

          <div class="form-group text-right">
            <button type="submit" class="btn btn-lg btn-primary callingly-button-color">
              Login
            </button>
          </div>

          <a
            :href="`${baseUrl}/apps/workos/login/google`"
            class="btn btn-default btn-sign-up btn-sign-up-google btn-lg rounded"
            style="position: relative; top: 70px"
          >
            <img
              src="./assets/google-sign-in.png"
              class="btn-sign-up-google__img"
              alt="Google logo"
            />
            Log in with Google
          </a>
        </form>
      </div>

      <div v-if="user?.id">
        <div
          class="text-center"
          v-if="!isDialpadOpen && !isTransferOpen && !isConferenceLeadOpen && !openTab"
        >
          <img
            v-if="lead"
            class="img-responsive"
            style="user-select: none; height: 80px; width: 80px; border-radius: 50%; margin: auto"
            :src="lead.profile_picture ?? 'https://ui-avatars.com/api/%20/160/81D5F2/073B4C/2/0.45'"
          />

          <h3>
            {{ lead.label }}
          </h3>
          <h3 v-if="status == 'waiting'">Waiting for lead...</h3>

          <button
            v-if="status == 'waiting' && online && registered && !lead.phone_number"
            type="button"
            class="btn btn-default"
            style="margin-top: 20px; padding: 6px 12px 6px 10px"
            :disabled="isChecking"
            @click="check()"
          >
            <i class="material-icons" style="font-size: 23px; float: left">person</i
            ><span style="margin-left: 7px; position: relative; top: 1px">Check for lead</span>
          </button>

          <p v-if="status != 'waiting'" style="font-size: 16px">
            {{ message }}
          </p>
          <!-- <p
            v-if="call_user.type == 'barge' || call_user.type == 'whisper' || call_user.type == 'listen' || call.type == 'transfer' || call.type == 'conference'"
            style="font-size: 16px;"
          >
            With {{ call.user.full_name }}
          </p>					 -->

          <div style="margin-top: 20px; margin-bottom: 20px; text-align: center">
            <h4 style="font-style: italic" v-if="lead.source">
              {{ lead.source }}
            </h4>
            <h4 v-if="call?.account_id == 9920 || call?.account_id == 6552">
              {{ lead.phone_number_formatted }}
            </h4>
            <h4 v-if="lead.email && call?.account_id != 6671 && call?.account_id != 10906">
              {{ lead.email }}
            </h4>
            <h4 v-if="lead.category">
              {{ lead.category }}
            </h4>
            <a
              v-if="lead.crm && crmLinks[lead.crm]"
              target="_blank"
              class="btn btn-default"
              :href="`https://callingly.com/dashboard/leads/${lead.id}/crm-redirect`"
              >{{ crmLinks[lead.crm] }} &nbsp;<i
                style="font-size: 18px; float: right; margin-top: 1px"
                class="material-icons"
                >link</i
              ></a
            >
            <a
              v-if="call?.account_id == 6671 && call?.profile_id == 12257"
              target="_blank"
              class="btn btn-default"
              :href="
                'https://agente.alige-seguro.com?agId=' +
                user.id +
                '&clId=' +
                lead.id +
                '&tel=' +
                lead.phone_number +
                '&callId=' +
                call.id
              "
              >Alige &nbsp;<i
                style="font-size: 18px; float: right; margin-top: 1px"
                class="material-icons"
                >link</i
              ></a
            >
            <a
              v-else-if="call?.account_id == 6671"
              target="_blank"
              class="btn btn-default"
              :href="
                'https://crm.alige.com.mx?agId=' +
                user.id +
                '&clId=' +
                lead.id +
                '&tel=' +
                lead.phone_number +
                '&callId=' +
                call.id
              "
              >Alige &nbsp;<i
                style="font-size: 18px; float: right; margin-top: 1px"
                class="material-icons"
                >link</i
              ></a
            >
            <a
              v-if="call?.account_id == 10906"
              target="_blank"
              class="btn btn-default"
              :href="
                'https://crm.xeguroo.com/Usuario/Callingly?agId=' +
                user.id +
                '&clId=' +
                lead.id +
                '&tel=' +
                lead.phone_number +
                '&callId=' +
                call.id
              "
              >Xeguroo &nbsp;<i
                style="font-size: 18px; float: right; margin-top: 1px"
                class="material-icons"
                >link</i
              ></a
            >
          </div>
        </div>

        <div
          v-if="!isConferenceLeadOpen"
          id="buttons"
          style="width: fit-content; margin: auto"
          class="clearfix"
        >
          <div
            v-if="status == 'ringing'"
            style="width: 70px; float: left; margin: 0 45px"
            @click="acceptCall()"
          >
            <div
              class="text-center"
              style="
                cursor: pointer;
                width: 70px;
                height: 70px;
                border-radius: 50%;
                border: 2px solid #5cb85c;
                margin: auto;
              "
            >
              <i class="material-icons" style="color: #5cb85c; font-size: 40px; line-height: 65px"
                >call</i
              >
            </div>
            <p style="font-size: 14px; text-align: center; padding-top: 10px">
              {{ call?.direction == 'inbound' || call?.type == 'transfer' ? 'Accept' : 'Call' }}
            </p>
          </div>

          <div
            v-if="status == 'ringing'"
            style="width: 70px; float: left; margin: 0 45px"
            @click="reject()"
          >
            <div
              class="text-center"
              style="
                cursor: pointer;
                width: 70px;
                height: 70px;
                border-radius: 50%;
                border: 2px solid #eb0000;
                margin: auto;
              "
            >
              <i class="material-icons" style="color: #eb0000; font-size: 40px; line-height: 65px"
                >call_end</i
              >
            </div>
            <p style="font-size: 14px; text-align: center; padding-top: 10px">Ignore</p>
          </div>

          <div
            v-if="
              status == 'oncall' &&
              call?.lead_id &&
              call_user?.type != 'listen' &&
              call_user?.type != 'whisper' &&
              isTransferStarted &&
              !isOnHold
            "
            class="text-center"
            style="cursor: pointer; width: 70px; float: right; margin: 0 10px"
            @click="hold()"
          >
            <div class="btn-dialer">
              <i
                class="material-icons"
                style="color: #073b4c; font-size: 40px; line-height: 60px; margin-left: -2px"
                >phone_paused</i
              >
            </div>
            <p style="font-size: 14px; text-align: center; padding-top: 10px">Hold</p>
          </div>

          <div
            v-if="
              status == 'oncall' &&
              call?.lead_id &&
              call_user?.type != 'listen' &&
              call_user?.type != 'whisper' &&
              isTransferStarted &&
              isOnHold
            "
            class="text-center"
            style="cursor: pointer; width: 70px; float: right; margin: 0 10px"
            @click="unhold()"
          >
            <div class="btn-dialer">
              <i
                class="material-icons"
                style="color: #073b4c; font-size: 40px; line-height: 60px; margin-left: -2px"
                >phone_in_talk</i
              >
            </div>
            <p style="font-size: 14px; text-align: center; padding-top: 10px">Resume</p>
          </div>

          <div
            v-if="
              status == 'oncall' &&
              call?.lead_id &&
              call_user?.type != 'listen' &&
              call_user?.type != 'whisper'
            "
            class="text-center"
            style="cursor: pointer; width: 70px; float: right; margin: 0 10px"
            @click="toggleTransfer()"
          >
            <div class="btn-dialer">
              <i
                class="material-icons"
                style="color: #073b4c; font-size: 40px; line-height: 60px; margin-left: -2px"
                >add_ic_call</i
              >
            </div>
            <p style="font-size: 14px; text-align: center; padding-top: 10px">Transfer</p>
          </div>

          <div
            v-if="status == 'oncall' && !call?.lead_id"
            class="text-center"
            style="cursor: pointer; width: 70px; float: right; margin: 0 10px"
            @click="toggleConferenceLead()"
          >
            <div class="btn-dialer">
              <i
                class="material-icons"
                style="
                  color: #073b4c;
                  font-size: 40px;
                  line-height: 60px;
                  margin-left: 3px;
                  margin-top: -2px;
                "
                >person_add_alt_1</i
              >
            </div>
            <p style="font-size: 14px; text-align: center; padding-top: 10px">Add Lead</p>
          </div>

          <div
            v-if="status == 'oncall' && call_user?.type != 'listen' && call_user?.type != 'whisper'"
            class="text-center"
            style="cursor: pointer; width: 70px; float: right; margin: 0 10px"
            @click="toggleDialpad()"
          >
            <div class="btn-dialer">
              <i class="material-icons" style="color: #073b4c; font-size: 40px; line-height: 60px"
                >dialpad</i
              >
            </div>
            <p style="font-size: 14px; text-align: center; padding-top: 10px">Keypad</p>
          </div>

          <div
            v-if="status == 'oncall' && !isMuted"
            class="text-center"
            style="cursor: pointer; width: 70px; float: right; margin: 0 10px"
            @click="mute()"
          >
            <div class="btn-dialer">
              <i class="material-icons" style="color: #073b4c; font-size: 40px; line-height: 60px"
                >volume_up</i
              >
            </div>
            <p style="font-size: 14px; text-align: center; padding-top: 10px">Mute</p>
          </div>

          <div
            v-if="status == 'oncall' && isMuted"
            :class="{ disabled: call_user?.type == 'listen' }"
            class="text-center"
            style="cursor: pointer; width: 70px; float: right; margin: 0 10px"
            @click="unmute()"
          >
            <div class="btn-dialer">
              <i class="material-icons" style="color: #073b4c; font-size: 40px; line-height: 60px"
                >volume_off</i
              >
            </div>
            <p style="font-size: 14px; text-align: center; padding-top: 10px">Muted</p>
          </div>

          <div
            v-if="status == 'oncall'"
            class="text-center"
            style="cursor: pointer; width: 70px; float: right; margin: 0 10px"
            @click="hangup()"
          >
            <div
              style="
                width: 60px;
                height: 60px;
                border-radius: 50%;
                border: 2px solid #eb0000;
                margin: auto;
              "
            >
              <i class="material-icons" style="color: #eb0000; font-size: 40px; line-height: 60px"
                >call_end</i
              >
            </div>
            <p style="font-size: 14px; text-align: center; padding-top: 10px">Hang Up</p>
          </div>
        </div>

        <div
          v-if="status == 'disposition' && !isDialpadOpen && !openTab"
          style="margin-top: 10px; margin-bottom: 30px; text-align: center"
        >
          <h3 style="font-weight: 400; margin-bottom: 20px; margin-top: 0" class="text-center">
            Call Result
          </h3>

          <button
            type="button"
            class="btn btn-default btn-disposition"
            style="background-color: transparent"
            @click="disposition('contacted')"
          >
            <i class="material-icons" style="color: #5cb85c; font-size: 25px">how_to_reg</i
            ><span style="line-height: 26px; vertical-align: top; margin-left: 7px">Contacted</span>
          </button>

          <button
            type="button"
            class="btn btn-default btn-disposition"
            style="background-color: transparent"
            @click="disposition('voicemail')"
          >
            <i class="material-icons" style="color: #f0ad4e; font-size: 25px">voicemail</i>
            <span style="line-height: 26px; vertical-align: top; margin-left: 7px">Voicemail</span>
          </button>

          <button
            type="button"
            class="btn btn-default btn-disposition"
            style="background-color: transparent"
            @click="disposition('missed')"
          >
            <i class="material-icons" style="color: #d9534f; font-size: 25px"
              >call_missed_outgoing</i
            >
            <span style="line-height: 26px; vertical-align: top; margin-left: 7px">Missed</span>
          </button>
          <br />
          <button
            type="button"
            class="btn btn-default btn-disposition"
            style="
              background-color: transparent;
              margin-top: 10px;
              padding-top: 5px;
              padding-bottom: 2px;
              padding-left: 10px;
              padding-right: 15px;
            "
            @click="disposition('removed')"
          >
            <i class="material-icons" style="color: #d9534f; font-size: 25px">close</i>
            <span style="line-height: 26px; vertical-align: top; margin-left: 2px">Remove</span>
          </button>

          <br />

          <button
            v-if="user.permissions.includes('app-retry')"
            type="button"
            class="btn btn-default btn-disposition"
            style="background-color: transparent; margin-top: 5px"
            @click="retry"
          >
            <i class="material-icons" style="color: #073b4c; font-size: 18px; line-height: 1.4"
              >repeat</i
            >
            <span style="line-height: 23px; vertical-align: top; margin-left: 7px">Retry</span>
          </button>
          <button
            v-if="user.permissions.includes('app-sms')"
            type="button"
            class="btn btn-default btn-disposition"
            style="background-color: transparent; margin-top: 5px"
            @click="showsms"
          >
            <i class="material-icons" style="color: #073b4c; font-size: 18px; line-height: 1.4"
              >sms</i
            >
            <span style="line-height: 23px; vertical-align: top; margin-left: 7px">SMS</span>
          </button>

          <p
            v-if="dispositionCountdown <= 10"
            style="margin-top: 20px; font-size: 14px; color: #666"
          >
            Closing in...<span style="font-weight: bold; color: #073b4c">{{ dispositionCountdown }}</span
            >s
          </p>
        </div>

        <div v-if="status == 'note' && !isDialpadOpen && !openTab" class="mt-4 px-4">
          <h3 style="font-weight: 400; margin-bottom: 20px; margin-top: 0" class="text-center">
            Add Note
          </h3>

          <div
            class="rounded-lg bg-white outline-1 -outline-offset-1 outline-gray-300 focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-blue-300"
          >
            <textarea
              id="note"
              name="note"
              style="font-size: 18px"
              v-model="note"
              rows="3"
              placeholder="Add a note..."
              class="block w-full resize-none bg-transparent px-4 py-4 text-base text-gray-900 placeholder:text-gray-400 sm:text-sm/6"
            />
          </div>
          <div class="mb-6"></div>
          <div class="flex justify-end">
            <button type="button" class="btn btn-link btn-lg" @click="skipNote">Skip</button>
            <button
              type="button"
              class="btn btn-primary btn-lg"
              :disabled="!note"
              @click="saveNote"
            >
              Save
            </button>
          </div>
        </div>

        <div
          v-if="status == 'sms' && !openTab"
          style="margin-top: 10px; margin-bottom: 30px; text-align: center"
        >
          <h3 style="font-weight: 400; margin-bottom: 20px; margin-top: 0" class="text-center">
            Send SMS
          </h3>

          <textarea
            v-model="smsbody"
            style="width: 90%; margin: auto; margin-bottom: 10px"
            class="form-control"
          />

          <button
            type="button"
            class="btn btn-default btn-disposition"
            style="background-color: transparent; margin-top: 5px"
            @click="sendsms"
          >
            <i class="material-icons" style="color: #5cb85c; font-size: 18px; line-height: 1.4"
              >send</i
            >
            <span style="line-height: 23px; vertical-align: top; margin-left: 7px">Send</span>
          </button>
          <button
            type="button"
            class="btn btn-default btn-disposition"
            style="background-color: transparent; margin-top: 5px"
            @click="thankyou"
          >
            <i class="material-icons" style="color: #d9534f; font-size: 18px; line-height: 1.4"
              >cancel</i
            >
            <span style="line-height: 23px; vertical-align: top; margin-left: 7px">Cancel</span>
          </button>
        </div>

        <div
          v-if="status == 'disposition2'"
          style="margin-top: 10px; margin-bottom: 30px; text-align: center"
        >
          <h3 style="font-weight: 400; margin-bottom: 20px; margin-top: 0" class="text-center">
            Tags & Stages
          </h3>

          <button
            v-for="tag in call?.profile?.tags"
            :key="tag.id"
            style="padding-top: 6px; border: none; color: white; margin: 5px"
            type="button"
            class="btn btn-default btn-disposition"
            :style="{ backgroundColor: tag.color }"
            @click="disposition2(tag.key)"
          >
            {{ tag.name }}
          </button>

          <div
            style="cursor: pointer; margin-top: 10px; text-decoration: underline; color: grey"
            @click="thankyou()"
          >
            Skip
          </div>
        </div>

        <div v-if="status == 'thankyou' && !isDialpadOpen && !openTab">
          <h3 class="text-center">Thank you!</h3>
          <div class="mb-17"></div>

          <div class="flex justify-between mb-8" style="width: 300px; margin: auto">
            <button type="button" class="btn btn-primary btn-note" @click="() => (status = 'note')">
              <i class="material-icons mr-4" style="font-size: 20px">description</i>Add Note
            </button>
            <button type="button" class="btn btn-success btn-note" @click="handleDoneClick">
              <i class="material-icons mr-4" style="font-size: 20px">check</i>Done
            </button>
          </div>
        </div>

        <div
          v-if="isDialpadOpen"
          style="
            color: white;
            width: fit-content;
            margin: auto;
            margin-top: 30px;
            overflow: hidden;
            height: auto;
          "
        >
          <div>
            <a class="btn btn-number" @click="digitPress('1')">1</a>
            <a class="btn btn-number" @click="digitPress('2')">2</a>
            <a class="btn btn-number" @click="digitPress('3')">3</a>
          </div>
          <div style="margin-top: 10px">
            <a class="btn btn-number" @click="digitPress('4')">4</a>
            <a class="btn btn-number" @click="digitPress('5')">5</a>
            <a class="btn btn-number" @click="digitPress('6')">6</a>
          </div>
          <div style="margin-top: 10px">
            <a class="btn btn-number" @click="digitPress('7')">7</a>
            <a class="btn btn-number" @click="digitPress('8')">8</a>
            <a class="btn btn-number" @click="digitPress('9')">9</a>
          </div>
          <div style="margin-top: 10px">
            <a class="btn btn-number" @click="digitPress('*')"
              ><span style="position: relative; top: 3px">*</span></a
            >
            <a class="btn btn-number" @click="digitPress('0')">0</a>
            <a class="btn btn-number" @click="digitPress('#')">#</a>
          </div>
        </div>

        <div v-if="isTransferOpen">
          <div
            style="
              padding-top: 15px;
              padding-left: 0;
              padding-right: 0;
              height: 430px;
              overflow: none;
              margin-top: -30px;
            "
          >
            <div style="text-align: center; font-weight: bold">
              Transfer to
              <select
                v-model="transfer_type"
                class="form-control"
                style="margin-bottom: 10px; margin-left: 5px; width: 125px; display: inline-block"
              >
                <option value="user">User</option>
                <option value="team">Team</option>
              </select>
            </div>

            <select
              v-if="transfer_type == 'user'"
              v-model="agents_status"
              class="form-control"
              style="margin-bottom: 10px; margin-left: 5px; width: 125px; display: inline-block"
              @change="getAgents()"
            >
              <option value="all">All Statuses</option>
              <option value="online">Available</option>
              <option value="oncall">On Call</option>
            </select>

            <select
              v-if="transfer_type == 'user'"
              v-model="team_id"
              class="form-control"
              style="margin-bottom: 10px; margin-left: 5; width: 125px; display: inline-block"
              @change="getAgents()"
            >
              <option :value="null">All Teams</option>
              <option v-for="option in teams" :key="option.id" :value="option.id">
                {{ option.name }}
              </option>
            </select>

            <input
              v-if="transfer_type == 'user'"
              v-model="agents_search"
              type="text"
              class="form-control pull-right"
              style="width: 180px; margin-bottom: 10px; margin-right: 5px"
              placeholder="Search"
              @keyup="getAgents()"
            />

            <table
              v-if="transfer_type == 'user'"
              class="table table-striped table-hover"
              style="margin-bottom: 0; background-color: white"
            >
              <tbody>
                <tr v-if="!agents.length">
                  <td colspan="3" style="background: white; border: none">
                    <p class="text-center">No agents found!</p>
                  </td>
                </tr>
                <tr v-for="agent in agents" :key="agent.id">
                  <td class="text-left" style="padding-left: 10px; width: 30px">
                    <i
                      v-if="agent.on_call_id"
                      title="On A Call"
                      class="material-icons"
                      style="color: #f0ad4e; font-size: 21px; display: block"
                      >do_not_disturb_on</i
                    >
                    <i
                      v-else-if="agent.is_available && (!agent.is_app_only || agent.is_online_app)"
                      title="Available"
                      class="material-icons"
                      style="color: green; font-size: 21px; display: block"
                      >check_circle</i
                    >
                    <i
                      v-else
                      title="Offline"
                      class="material-icons"
                      style="color: #d9534f; font-size: 21px; display: block"
                      >cancel</i
                    >
                  </td>
                  <td
                    style="
                      white-space: nowrap;
                      overflow: hidden;
                      text-overflow: ellipsis;
                      max-width: 150px;
                    "
                  >
                    {{ agent.full_name }}
                  </td>
                  <td class="text-right" style="padding-right: 10px">
                    <i
                      v-if="
                        !agent.on_call_id &&
                        agent.is_available &&
                        (!agent.is_app_only || agent.is_online_app)
                      "
                      class="material-icons"
                      style="float: right; font-size: 22px; color: #5cb85c; cursor: pointer"
                      @click="startTransfer(agent, null)"
                      >call</i
                    >
                  </td>
                </tr>
              </tbody>
            </table>
            <div v-if="transfer_type == 'team'" style="height: 350px; overflow-y: scroll">
              <table
                class="table table-striped table-hover"
                style="margin-bottom: 0; background-color: white"
              >
                <tbody>
                  <tr v-if="!teams.length">
                    <td colspan="3" style="background: white; border: none">
                      <p class="text-center">No teams found!</p>
                    </td>
                  </tr>
                  <tr v-for="team in teams" :key="team.id">
                    <td class="text-left" style="padding-left: 10px; width: 30px">
                      <i
                        v-if="team.is_available"
                        title="Available"
                        class="material-icons"
                        style="color: green; font-size: 21px; display: block"
                        >check_circle</i
                      >
                      <i
                        v-else
                        title="Offline"
                        class="material-icons"
                        style="color: #d9534f; font-size: 21px; display: block"
                        >cancel</i
                      >
                    </td>
                    <td
                      style="
                        white-space: nowrap;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        max-width: 150px;
                      "
                    >
                      {{ team.name }}
                    </td>
                    <td class="text-right" style="padding-right: 10px">
                      <i
                        v-if="team.is_available"
                        class="material-icons"
                        style="float: right; font-size: 22px; color: #5cb85c; cursor: pointer"
                        @click="startTransfer(null, team)"
                        >call</i
                      >
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <ul
              v-if="transfer_type == 'user'"
              class="pager"
              style="
                padding-left: 20px;
                padding-right: 20px;
                position: absolute;
                right: 0;
                left: 0;
                bottom: 5px;
              "
            >
              <li :class="{ disabled: agents_meta.current_page < 2 }" class="previous">
                <a
                  style="border-radius: 4px"
                  href="#"
                  rel="previous"
                  @click="getAgents(agents_meta.current_page - 1)"
                  >Previous</a
                >
              </li>
              <li
                :class="{
                  disabled: agents_meta.current_page == agents_meta.last_page,
                }"
                class="next"
              >
                <a
                  style="border-radius: 4px"
                  href="#"
                  rel="next"
                  @click="getAgents(agents_meta.current_page + 1)"
                  >Next</a
                >
              </li>
            </ul>
          </div>
        </div>

        <div v-if="openTab == 'conference'">
          <i
            style="font-size: 30px; top: 80px; position: absolute; right: 10px; cursor: pointer"
            class="material-icons"
            @click="toggleConference"
            >close</i
          >

          <div
            style="
              background: white;
              padding: 0;
              height: 400px;
              overflow: none;
              margin-bottom: 10px;
            "
          >
            <h3 class="text-center" style="margin-top: 0px; margin-bottom: 20px">Conference</h3>

            <select
              v-model="agents_status"
              class="form-control"
              style="margin-bottom: 5px; margin-left: 5px; width: 125px; display: inline-block"
              @change="getAgents()"
            >
              <option value="all">All Statuses</option>
              <option value="online">Online</option>
              <option value="oncall">On Call</option>
            </select>

            <select
              v-model="team_id"
              class="form-control"
              style="margin-bottom: 5px; margin-left: 5; width: 125px; display: inline-block"
              @change="getAgents()"
            >
              <option :value="null">All Teams</option>
              <option v-for="option in teams" :key="option.id" :value="option.id">
                {{ option.name }}
              </option>
            </select>

            <input
              v-model="agents_search"
              type="text"
              class="form-control pull-right"
              style="width: 180px; margin-bottom: 5px; margin-right: 5px"
              placeholder="Search"
              @keyup="getAgents()"
            />

            <table
              class="table table-striped table-hover"
              style="margin-bottom: 0; background-color: white"
            >
              <tbody>
                <tr v-if="!agents.length">
                  <td colspan="3" style="background: white; border: none">
                    <p class="text-center">No agents found!</p>
                  </td>
                </tr>
                <tr v-for="agent in agents" :key="agent.id">
                  <td class="text-left" style="padding-left: 10px; width: 30px">
                    <i
                      v-if="agent.on_call_id"
                      title="On A Call"
                      class="material-icons"
                      style="color: #f0ad4e; font-size: 21px; display: block"
                      >do_not_disturb_on</i
                    >
                    <i
                      v-else-if="agent.is_available && (agent.is_online_app || agent.is_mobile)"
                      title="Available"
                      class="material-icons"
                      style="color: green; font-size: 21px; display: block"
                      >check_circle</i
                    >
                    <i
                      v-else
                      title="Offline"
                      class="material-icons"
                      style="color: #d9534f; font-size: 21px; display: block"
                      >cancel</i
                    >
                  </td>
                  <td
                    style="
                      white-space: nowrap;
                      overflow: hidden;
                      text-overflow: ellipsis;
                      max-width: 150px;
                    "
                  >
                    {{ agent.full_name }}
                  </td>
                  <td class="text-right" style="padding-right: 10px">
                    <i
                      v-if="agent.on_call_id && user.permissions.includes('listen')"
                      class="material-icons"
                      style="float: right; font-size: 22px; cursor: pointer"
                      @click="startListen(agent.on_call_id)"
                      >hearing</i
                    >
                    <i
                      v-if="agent.on_call_id && user.permissions.includes('whisper')"
                      class="material-icons"
                      style="float: right; font-size: 22px; cursor: pointer; margin-right: 10px"
                      @click="startWhisper(agent.on_call_id)"
                      >headset_mic</i
                    >
                    <i
                      v-if="agent.on_call_id && user.permissions.includes('barge')"
                      class="material-icons"
                      style="float: right; font-size: 22px; cursor: pointer; margin-right: 10px"
                      @click="startBarge(agent.on_call_id)"
                      >call</i
                    >
                    <i
                      v-if="
                        !agent.on_call_id &&
                        agent.is_available &&
                        (agent.is_online_app || agent.is_mobile)
                      "
                      class="material-icons"
                      style="float: right; font-size: 22px; color: #5cb85c; cursor: pointer"
                      @click="startConference(agent)"
                      >call</i
                    >
                  </td>
                </tr>
              </tbody>
            </table>
            <ul
              class="pager"
              style="
                padding-left: 20px;
                padding-right: 20px;
                position: absolute;
                right: 0;
                left: 0;
                bottom: 65px;
              "
            >
              <li :class="{ disabled: agents_meta.current_page < 2 }" class="previous">
                <a
                  style="border-radius: 4px"
                  href="#"
                  rel="previous"
                  @click="getAgents(agents_meta.current_page - 1)"
                  >Previous</a
                >
              </li>
              <li
                :class="{
                  disabled: agents_meta.current_page == agents_meta.last_page,
                }"
                class="next"
              >
                <a
                  style="border-radius: 4px"
                  href="#"
                  rel="next"
                  @click="getAgents(agents_meta.current_page + 1)"
                  >Next</a
                >
              </li>
            </ul>
          </div>
        </div>

        <div v-if="openTab == 'settings'">
          <i
            style="font-size: 30px; top: 80px; position: absolute; right: 10px; cursor: pointer"
            class="material-icons"
            @click="toggleSettings"
            >close</i
          >
          <h3 class="text-center" style="margin-top: 0px; margin-bottom: 30px">Settings</h3>

          <div style="padding-left: 30px; padding-right: 30px; height: 400px">
            <h4>Microphone</h4>
            <select v-model="audio.microphoneDevice" class="form-control">
              <option v-for="option in audio.input" :key="option.deviceId" :value="option.deviceId">
                {{ option.label }}
              </option>
            </select>

            <h4>Speaker</h4>
            <select v-model="audio.speakerDevice" class="form-control">
              <option
                v-for="option in audio.output"
                :key="option.deviceId"
                :value="option.deviceId"
              >
                {{ option.label }}
              </option>
            </select>

            <h4>Ringer</h4>
            <select v-model="audio.ringtoneDevice" class="form-control">
              <option
                v-for="option in audio.output"
                :key="option.deviceId"
                :value="option.deviceId"
              >
                {{ option.label }}
              </option>
            </select>

            <div v-if="user.permissions.includes('app-autoaccept')">
              <h4>Auto Accept</h4>
              <div class="material-switch">
                <span class="small-label" style="padding-right: 6px">OFF</span>
                <input id="autoaccept" v-model="autoaccept" value="1" type="checkbox" />
                <label
                  for="autoaccept"
                  class="label-success"
                  style="
                    background-color: #167296;
                    position: relative;
                    top: 0;
                    left: 0;
                    pointer-events: all;
                  "
                />
                <span class="small-label" style="padding-left: 3px">ON</span>
              </div>
            </div>

            <h4>Version</h4>
            <p style="font-size: 12px" @click="checkVersion">v{{ version }}</p>
          </div>
        </div>

        <div v-if="openTab == 'history'">
          <TabHistory @close="toggleHistory" @new-call="toggleNewCall" />
        </div>

        <div v-if="isConferenceLeadOpen">
          <i
            style="font-size: 30px; top: 80px; position: absolute; right: 10px; cursor: pointer"
            class="material-icons"
            @click="toggleConferenceLead"
            >close</i
          >
          <form role="form" style="padding: 30px" @submit.prevent="callConferenceLead">
            <h3 class="text-center" style="margin-top: 0px; margin-bottom: 30px">Add Lead</h3>

            <div
              v-if="error_message"
              class="help-block text-center"
              style="color: #a94442; margin-top: 0px; margin-bottom: 10px"
            >
              <strong>{{ error_message }} </strong>
            </div>

            <div class="row">
              <div class="col-xs-6">
                <div class="form-group">
                  <label for="name" class="control-label">First Name</label>

                  <input
                    id="fname"
                    v-model="fname"
                    style="font-size: 18px; padding: 22px 10px"
                    type="text"
                    class="form-control"
                  />
                </div>
              </div>

              <div class="col-xs-6">
                <div class="form-group">
                  <label for="name" class="control-label">Last Name</label>

                  <input
                    id="lname"
                    v-model="lname"
                    style="font-size: 18px; padding: 22px 10px"
                    type="text"
                    class="form-control"
                  />
                </div>
              </div>
            </div>

            <div class="row">
              <div class="col-sm-12">
                <div class="form-group">
                  <label for="name" class="control-label">Phone Number</label>
                  <div class="clearfix">
                    <vue-tel-input
                      v-model="phone_number"
                      :default-country="user.country_code"
                      :show-dial-code="true"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div class="form-group clearfix" style="margin-top: 20px; margin-bottom: 0">
              <button
                id="submit"
                type="submit"
                class="btn btn-primary pull-right"
                style="padding: 10px 20px; border-radius: 2px; border: none"
              >
                Call
              </button>
            </div>
          </form>
        </div>

        <div v-show="openTab == 'new_call'">
          <i
            style="font-size: 30px; top: 80px; position: absolute; right: 10px; cursor: pointer"
            class="material-icons"
            @click="() => toggleNewCall()"
            >close</i
          >
          <h3 class="text-center" style="margin-top: 0px; margin-bottom: 30px">New Call</h3>

          <form
            role="form"
            style="padding-left: 30px; padding-right: 30px; height: 400px"
            @submit.prevent="makeOutboundCall"
          >
            <div
              v-if="error_message"
              class="help-block text-center"
              style="color: #a94442; margin-top: 0px; margin-bottom: 10px"
            >
              <strong>{{ error_message }} </strong>
            </div>

            <div class="row">
              <div class="col-xs-6">
                <div class="form-group">
                  <label for="name" class="control-label">First Name</label>

                  <input
                    id="fname"
                    v-model="fname"
                    style="font-size: 18px; padding: 22px 10px"
                    type="text"
                    class="form-control"
                  />
                </div>
              </div>

              <div class="col-xs-6">
                <div class="form-group">
                  <label for="name" class="control-label">Last Name</label>

                  <input
                    id="lname"
                    v-model="lname"
                    style="font-size: 18px; padding: 22px 10px"
                    type="text"
                    class="form-control"
                  />
                </div>
              </div>
            </div>

            <div class="row">
              <div class="col-sm-12">
                <div class="form-group">
                  <label for="name" class="control-label">Phone Number</label>
                  <div class="clearfix">
                    <vue-tel-input
                      v-model="phone_number"
                      :default-country="user.country_code"
                      :show-dial-code="true"
                      :onlyCountries="user.account.countries"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div class="row" v-if="caller_ids.length > 0">
              <div class="col-sm-12">
                <div class="form-group">
                  <div
                    style="
                      display: flex;
                      flex-direction: column;
                      align-items: center;
                      justify-content: center;
                    "
                  >
                    <label
                      for="name"
                      style="display: flex; align-items: center"
                      class="control-label"
                      >Caller ID</label
                    >
                    <div style="display: flex; align-items: center; justify-content: center">
                      <i
                        class="material-icons"
                        style="float: left; color: #073b4c; font-size: 16px; margin-right: 2px"
                        >phone</i
                      >
                      <select
                        v-model="caller_id"
                        style="
                          border: none;
                          background-color: transparent;
                          box-shadow: none;
                          display: inline-block;
                        "
                        :class="[caller_id === null ? 'caller-id-default-state' : '']"
                      >
                        <option :value="null">Default</option>
                        <option
                          v-for="caller_id in caller_ids"
                          :key="caller_id.id"
                          :value="caller_id.id"
                        >
                          {{
                            caller_id.name.length > 40
                              ? caller_id.name.substring(0, 40) + '...'
                              : caller_id.name
                          }}:
                          {{ caller_id.phone_number_formatted }}
                        </option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="form-group clearfix" style="margin-top: 20px; margin-bottom: 0">
              <button
                id="submit"
                type="submit"
                class="btn btn-primary pull-right"
                style="padding: 10px 20px; border-radius: 2px; border: none"
                :disabled="isMakingCall || !online || !registered"
              >
                Call Now
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <div
      v-if="user?.id && !isTransferOpen && !isConferenceLeadOpen && !openTab"
      style="position: absolute; bottom: 10px; right: 0; left: 0"
    >
      <div v-if="status == 'oncall'" style="width: fit-content; margin: auto">
        <div style="float: left; margin-right: 10px">
          <i
            class="material-icons"
            style="float: left; color: #073b4c; font-size: 12px; margin-right: 2px"
            >headset</i
          >
          <div
            v-for="index in 5"
            :key="index"
            style="
              width: 7px;
              height: 5px;
              margin-top: 2px;
              float: left;
              border-radius: 2px;
              margin-left: 1px;
            "
            :style="{
              backgroundColor: index < audio.outputVolume ? '#5cb85c' : '#D3D3D3',
            }"
          />
        </div>

        <div style="float: left">
          <i class="material-icons" style="float: left; color: #073b4c; font-size: 12px">mic</i>
          <div
            v-for="index in 5"
            :key="index"
            style="
              width: 7px;
              height: 5px;
              margin-top: 2px;
              float: left;
              border-radius: 2px;
              margin-left: 1px;
            "
            :style="{
              backgroundColor: index < audio.inputVolume ? '#5cb85c' : '#D3D3D3',
            }"
          />
        </div>
      </div>
    </div>

    <nav v-if="user && status == 'waiting'" class="mobile-bottom-nav">
      <div class="mobile-bottom-nav__item" @click="toggleNewCall()">
        <div class="mobile-bottom-nav__item-content">
          <i class="material-icons">phone</i>
          Call
        </div>
      </div>
      <div class="mobile-bottom-nav__item" @click="toggleConference()" style="display: none">
        <div class="mobile-bottom-nav__item-content">
          <i class="material-icons">people</i>
          Conference
        </div>
      </div>
      <div class="mobile-bottom-nav__item" @click="toggleHistory()">
        <div class="mobile-bottom-nav__item-content">
          <i class="material-icons">history</i>
          History
        </div>
      </div>

      <div class="mobile-bottom-nav__item" @click="toggleSettings()">
        <div class="mobile-bottom-nav__item-content">
          <i class="material-icons">settings</i>
          Settings
        </div>
      </div>
    </nav>
  </main>
</template>

<style scoped>
.main-container {
  background: url('./assets/background.png');
}

.btn-sign-up {
  width: 100%;
  padding: 15px 18px;
  border: none;
  font-size: 14px;
}

.btn-sign-up-google {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  color: #636363;
  outline: none;
  border: none;
  border-radius: 0;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.25);
}

.btn-sign-up-google__img {
  position: absolute;
  left: 2rem;
  width: 20px;
}

.form-control {
  border-radius: 0;
}

.pager {
  margin: 0;
}

[v-cloak] {
  display: none;
}

.callingly-button-color {
  background-color: #32a8d9;
  border-color: #32a8d9;
}

.small-label {
  color: #808080;
  font-weight: bold;
  font-size: 12px;
  text-transform: uppercase;
}

.btn-disposition,
.btn-tag {
  font-size: 15px;
  color: #073b4c;
  padding-left: 20px;
  padding-right: 20px;
  padding-top: 9px;
}

.btn-note {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 18px;
  width: 135px;
}

.btn-number {
  font-family: 'Lato', sans-serif;
  font-size: 21px;
  color: #073b4c;
  border: solid 1px #073b4c;
  margin: 3px;
  position: relative;
  border-radius: 50%;
  width: 55px;
  height: 55px;
  background: transparent;
  padding: 12px;
}

.btn-dialer {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  border: 2px solid #073b4c;
  margin: auto;
}

.disabled > .btn-dialer {
  border: 2px solid #d3d3d3;
}

.disabled > .btn-dialer > .material-icons,
.disabled > p {
  color: #d3d3d3 !important;
}

.btn-number:hover {
  color: #57bbe4;
  border: 1px solid #57bbe4;
}

body {
  color: #073b4c;
  overflow: hidden;
}

.disabled {
  pointer-events: none;
}

.row {
  height: initial;
}

.vue-tel-input {
  background: white;
}

.material-switch {
  margin-top: 3px;
}
.material-switch > input[type='checkbox'] {
  display: none;
}
.material-switch > label {
  cursor: pointer;
  height: 0;
  position: relative;
  width: 40px;
}
.material-switch > label::before {
  background: #000;
  box-shadow: inset 0 0 10px rgba(0, 0, 0, 0.5);
  border-radius: 8px;
  content: '';
  height: 16px;
  margin-top: -8px;
  position: absolute;
  opacity: 0.3;
  transition: all 0.4s ease-in-out;
  width: 40px;
}
.material-switch > label::after {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.3);
  content: '';
  height: 24px;
  left: -4px;
  margin-top: -8px;
  position: absolute;
  top: -4px;
  transition: all 0.3s ease-in-out;
  width: 24px;
}
.material-switch > input[type='checkbox']:checked + label::before {
  background: inherit;
  opacity: 0.5;
}
.material-switch > input[type='checkbox']:checked + label::after {
  background: inherit;
  left: 20px;
}

.add-triangle:after {
  position: absolute;
  top: -8px;
  right: 15px;
  display: inline-block;
  border-right: 10px solid transparent;
  border-bottom: 10px solid white;
  border-left: 10px solid transparent;
  content: '';
}

.form-group {
  position: relative;
  margin-bottom: 35px;
}
:deep(input) {
  font-size: 18px;
  padding: 10px 10px 10px 5px;
  display: block;
  width: 100%;
  border: 1px solid #cccccc;
}
.caller-id-default-state {
  width: 70px;
}
:deep(.vue-tel-input input) {
  border: none !important;
}
input:focus {
  outline: none;
}

.login label {
  color: #999;
  font-size: 18px;
  font-weight: normal;
  position: absolute;
  pointer-events: none;
  left: 5px;
  top: 10px;
  transition: 0.2s ease all;
  -moz-transition: 0.2s ease all;
  -webkit-transition: 0.2s ease all;
}

input:focus ~ label,
input:valid ~ label {
  top: -20px;
  font-size: 14px;
  color: #003c4d;
}

.bar {
  position: relative;
  display: block;
  width: 100%;
}
.bar:before,
.bar:after {
  content: '';
  height: 2px;
  width: 0;
  bottom: 1px;
  position: absolute;
  background: #003c4d;
  transition: 0.2s ease all;
  -moz-transition: 0.2s ease all;
  -webkit-transition: 0.2s ease all;
}
.bar:before {
  left: 50%;
}
.bar:after {
  right: 50%;
}

/* active state */
input:focus ~ .bar:before,
input:focus ~ .bar:after {
  width: 50%;
}

*:focus {
  outline: none;
}

.mobile-bottom-nav {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  will-change: transform;
  transform: translateZ(0);
  display: flex;
  height: 60px;
  box-shadow:
    0 5px 5px -3px rgb(0 0 0 / 20%),
    0 8px 10px 1px rgb(0 0 0 / 14%),
    0 3px 14px 2px rgb(0 0 0 / 12%);
  background-color: #fff;
}
.mobile-bottom-nav__item {
  flex-grow: 1;
  text-align: center;
  font-size: 12px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  cursor: pointer;
}

.mobile-bottom-nav__item .material-icons {
  margin-bottom: 5px;
}

.mobile-bottom-nav__item--active {
  color: red;
}
.mobile-bottom-nav__item-content {
  display: flex;
  flex-direction: column;
}
</style>
